import React, { useMemo, useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, format } from 'date-fns';
import { DOCUMENT } from '../constants/documentTheme';
import MapLink from './MapLink';
import FlightLegStatus from './FlightLegStatus';
import {
  isFlightItineraryItem,
  getFlightCardTitle,
  getFlightNumberLabel,
  getFlightLegRouteLabel,
} from '../utils/flightRouteUtils';
import {
  groupFlightsIntoJourneys,
  getJourneyCityRouteLabel,
  getJourneyReservationSubtitle,
  getJourneyLayoverLabel,
  getEnrichedJourneyLegs,
} from '../utils/flightJourneyUtils';
import { resolveFlightIataParts } from '../utils/liveFlightStatus';
import {
  isCarRentalItineraryItem,
  getRentalCardTitle,
  getRentalCardSubtitle,
} from '../utils/rentalRouteUtils';
import {
  formatTime24,
  formatDurationLabel,
  getCategoryEyebrow,
  getConfirmationRef,
  getReservationStatus,
  isCruiseItineraryItem,
  isReservationOnlyItem,
  parseTransportData,
  toLocalDate,
} from '../utils/itineraryDocumentUtils';
import {
  getDefaultItineraryItemImageUrl,
  getItineraryItemImageUrl,
  CARD_IMAGE_PARAMS,
} from '../utils/itineraryItemImage';
import ItinerarySafeImage from './ItinerarySafeImage';

const formatShort = (datetimeStr) => {
  const date = toLocalDate(datetimeStr);
  if (!date) return null;
  const time = formatTime24(datetimeStr);
  return time ? `${format(date, 'EEE d MMM')} · ${time}` : format(date, 'EEE d MMM');
};

const cardActionSx = {
  border: `1px solid ${DOCUMENT.ruleHard}`,
  bgcolor: DOCUMENT.surface,
  color: DOCUMENT.ink,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  px: 1.25,
  py: 0.75,
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { borderColor: DOCUMENT.accent, color: DOCUMENT.oxblood },
};

const CardActionButton = ({ testId, label, onClick, icon = null }) => (
  <Box
    component="button"
    type="button"
    data-testid={testId}
    aria-label={label}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick(event);
    }}
    onKeyDown={(event) => event.stopPropagation()}
    sx={icon ? {
      ...cardActionSx,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      px: 0,
      py: 0,
    } : cardActionSx}
  >
    {icon || label}
  </Box>
);

const ReservationCard = ({
  eyebrow,
  status,
  title,
  subtitle,
  summary,
  imageUrl,
  fallbackSrc,
  legs = null,
  rows,
  footerLeft,
  footerRight,
  onEdit,
  editLabel,
  onRemove,
  removeLabel,
  onSelectItem,
  detailsLabel,
  flightStatusEnabled = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded((open) => !open);

  return (
  <Box
    component="div"
    role="button"
    tabIndex={0}
    aria-expanded={expanded}
    onClick={toggleExpanded}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleExpanded();
      }
    }}
    data-testid="reservation-card"
    sx={{
      background: DOCUMENT.surface,
      border: `1px solid ${DOCUMENT.cardRule}`,
      borderRadius: 0,
      p: 0,
      textAlign: 'left',
      font: 'inherit',
      color: 'inherit',
      cursor: 'pointer',
      display: 'flex',
      width: '100%',
      overflow: 'hidden',
      '&:hover': { borderColor: DOCUMENT.accent },
    }}
  >
    <Box sx={{
      width: 56,
      minHeight: expanded ? 88 : 56,
      flexShrink: 0,
      bgcolor: DOCUMENT.oxblood,
      overflow: 'hidden',
      position: 'relative',
      alignSelf: 'stretch',
    }}>
      <ItinerarySafeImage
        src={imageUrl}
        fallbackSrc={fallbackSrc}
        alt=""
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </Box>
    <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.25 }}>
      <Typography sx={{
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: DOCUMENT.accent,
        pt: 0.5,
      }}>
        {eyebrow}
      </Typography>
      <Box
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}
      >
        {status && (
          <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3, mr: 0.25 }}>{status}</Typography>
        )}
        {onSelectItem && (
          <CardActionButton testId="reservation-details" label={detailsLabel} onClick={onSelectItem} />
        )}
        {onEdit && (
          <CardActionButton
            testId="reservation-edit"
            label={editLabel}
            onClick={onEdit}
            icon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
          />
        )}
        {onRemove && (
          <CardActionButton
            testId="reservation-remove"
            label={removeLabel}
            onClick={onRemove}
            icon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
          />
        )}
      </Box>
    </Box>
    <Typography
      data-testid="reservation-card-title"
      sx={{
        fontFamily: DOCUMENT.display,
        fontSize: 15,
        fontWeight: 400,
        lineHeight: 1.2,
      }}
    >
      {title}
    </Typography>
    {!expanded && (summary || typeof subtitle === 'string') && (
      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mt: 0.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {[summary, typeof subtitle === 'string' ? subtitle : null].filter(Boolean).join(' · ')}
      </Typography>
    )}
    <Collapse in={expanded} timeout={220} unmountOnExit>
      <Box sx={{ pt: 0.5, pb: 0.5 }}>
        {subtitle && (
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink2, mb: legs && legs.length ? 0.35 : 0.5 }}>{subtitle}</Typography>
        )}
        {legs && legs.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {legs.map((leg) => (
              <Box
                key={leg.key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  py: 0.85,
                  borderTop: `1px solid ${DOCUMENT.rule}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink, lineHeight: 1.3 }}>
                    {leg.route}
                  </Typography>
                  {leg.number && (
                    <Box sx={{ mt: 0.25 }}>
                      <FlightLegStatus
                        airline={leg.airline}
                        flightNumber={leg.flightNumber}
                        displayNumber={leg.number}
                        departureTime={leg.departureTime}
                        arrivalTime={leg.arrivalTime}
                        departureAirport={leg.departureAirport}
                        arrivalAirport={leg.arrivalAirport}
                        compact
                        enabled={flightStatusEnabled}
                      />
                    </Box>
                  )}
                </Box>
                {leg.time && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12,
                      color: DOCUMENT.ink3,
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      pt: 0.15,
                    }}
                  >
                    {leg.time}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
        {rows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1.5,
              py: 0.5,
              fontSize: 13,
              borderTop: `1px solid ${DOCUMENT.rule}`,
            }}
          >
            <Typography component="dt" sx={{ color: DOCUMENT.ink2, fontSize: 13 }}>{row.label}</Typography>
            <Typography component="dd" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, m: 0 }}>{row.value}</Typography>
          </Box>
        ))}
        {(footerLeft || footerRight) && (
          <Box sx={{ borderTop: `1px solid ${DOCUMENT.rule}`, mt: 1, pt: 1, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: DOCUMENT.ink3 }}>
            <span>{footerLeft}</span>
            <span>{footerRight}</span>
          </Box>
        )}
      </Box>
    </Collapse>
    </Box>
  </Box>
  );
};

const ItineraryReservations = ({
  items = [],
  onSelectItem,
  onEditItem,
  onRemoveItem,
  canEditItem,
  flightStatusEnabled = true,
}) => {
  const { t } = useTranslation();

  const cards = useMemo(() => {
    const result = [];
    const flights = items.filter(isFlightItineraryItem);
    const journeys = groupFlightsIntoJourneys(flights);

    journeys.forEach((journey, index) => {
      const first = journey.items?.[0];
      const last = journey.items?.[journey.items.length - 1] || first;
      if (!first) return;
      const title = getJourneyCityRouteLabel(journey) || getFlightCardTitle(first);
      const viaCity = getJourneyLayoverLabel(journey);
      const enrichedLegs = getEnrichedJourneyLegs(journey, first);
      const legLines = enrichedLegs.length > 0
        ? enrichedLegs.map((leg, legIndex) => {
            const depTime = leg.departure_time ? formatTime24(leg.departure_time) : null;
            const arrTime = leg.arrival_time ? formatTime24(leg.arrival_time) : null;
            const parts = resolveFlightIataParts({
              airline: leg.airline,
              airlineCode: leg.airline_code,
              flightNumber: leg.flight_number,
            });
            const airlineCode = parts?.airline || '';
            const flightNum = parts?.flight_number || '';
            const displayNumber = airlineCode && flightNum
              ? `${airlineCode} ${flightNum}`
              : ([leg.airline, leg.flight_number].filter(Boolean).join(' ') || null);
            return {
              key: `leg-${first.id}-${legIndex}`,
              route: getFlightLegRouteLabel(leg, legIndex, enrichedLegs),
              number: displayNumber,
              airline: airlineCode || leg.airline || '',
              flightNumber: flightNum || leg.flight_number || '',
              departureTime: leg.departure_time || first.start_datetime || null,
              arrivalTime: leg.arrival_time || last?.end_datetime || null,
              departureAirport: leg.departure_airport || null,
              arrivalAirport: leg.arrival_airport || null,
              time: depTime || arrTime ? `${depTime || '—'} – ${arrTime || '—'}` : null,
            };
          })
        : null;
      // When legs are listed, keep the subtitle light (via only) — flight numbers live on each leg.
      const subtitle = legLines?.length
        ? (viaCity
          ? t('itinerary.viaCity', { city: viaCity, defaultValue: `via ${viaCity}` })
          : null)
        : (getJourneyReservationSubtitle(journey) || getFlightNumberLabel(first));
      const confirmation = getConfirmationRef(first);
      const duration = formatDurationLabel(first.start_datetime, last?.end_datetime);
      let eyebrow;
      if (journeys.length === 1) {
        eyebrow = t('itinerary.outboundFlight');
      } else if (journeys.length === 2) {
        eyebrow = index === 0 ? t('itinerary.outboundFlight') : t('itinerary.returnFlight');
      } else {
        eyebrow = t('itinerary.outboundFlightIndexed', { index: index + 1 });
      }
      result.push({
        key: `flight-${first.id}-${index}`,
        itemId: first.id,
        imageUrl: getItineraryItemImageUrl(first, CARD_IMAGE_PARAMS),
        fallbackSrc: getDefaultItineraryItemImageUrl(first, CARD_IMAGE_PARAMS),
        eyebrow,
        status: legLines?.length
          ? null
          : getReservationStatus(last || first, 'flight', t),
        title,
        subtitle,
        summary: [formatShort(first.start_datetime), formatShort(last?.end_datetime)].filter(Boolean).join(' → ')
          || duration,
        legs: legLines,
        rows: [
          first.start_datetime && { label: t('itinerary.departs'), value: formatShort(first.start_datetime) },
          last?.end_datetime && { label: t('itinerary.arrives'), value: formatShort(last.end_datetime) },
        ].filter(Boolean),
        footerLeft: confirmation ? t('itinerary.refValue', { ref: confirmation }) : null,
        footerRight: duration,
      });
    });

    items.filter(isCarRentalItineraryItem).forEach((item) => {
      const data = parseTransportData(item);
      const start = toLocalDate(item.start_datetime);
      const end = toLocalDate(item.end_datetime);
      const days = start && end ? Math.max(1, differenceInCalendarDays(end, start)) : null;
      const confirmation = getConfirmationRef(item);
      result.push({
        key: `rental-${item.id}`,
        itemId: item.id,
        imageUrl: getItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
        fallbackSrc: getDefaultItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
        eyebrow: t('itinerary.vehicle'),
        status: getReservationStatus(item, 'rental', t),
        title: getRentalCardTitle(item),
        subtitle: getRentalCardSubtitle(item),
        summary: [formatShort(item.start_datetime), formatShort(item.end_datetime)].filter(Boolean).join(' → '),
        rows: [
          item.start_datetime && { label: t('itinerary.collected'), value: formatShort(item.start_datetime) },
          item.end_datetime && { label: t('itinerary.returnedOn'), value: formatShort(item.end_datetime) },
          data.car_type && { label: t('itinerary.vehicle'), value: data.car_type },
        ].filter(Boolean),
        footerLeft: confirmation ? t('itinerary.refValue', { ref: confirmation }) : null,
        footerRight: days != null ? t('itinerary.day', { count: days }) : null,
      });
    });

    items.filter(isCruiseItineraryItem).forEach((item, index, cruiseItems) => {
      const data = parseTransportData(item);
      const start = toLocalDate(item.start_datetime);
      const end = toLocalDate(item.end_datetime);
      const nights = start && end ? Math.max(1, differenceInCalendarDays(end, start)) : null;
      const confirmation = getConfirmationRef(item);
      const ports = Array.isArray(data.ports_of_call)
        ? data.ports_of_call.map((port) => String(port).trim()).filter(Boolean)
        : [];
      const portsLabel = ports.length > 2
        ? t('itinerary.viaPorts', {
          ports: ports.slice(1, -1).join(', '),
          defaultValue: `via ${ports.slice(1, -1).join(', ')}`,
        })
        : null;
      const embarkPort = data.departure_port || item.location || null;
      const shipTitle = data.ship_name
        ? (data.cruise_line ? `${data.cruise_line} ${data.ship_name}` : data.ship_name)
        : item.name;
      result.push({
        key: `cruise-${item.id}`,
        itemId: item.id,
        imageUrl: getItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
        fallbackSrc: getDefaultItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
        eyebrow: cruiseItems.length > 1
          ? t('itinerary.cruiseIndexed', {
            current: index + 1,
            total: cruiseItems.length,
            defaultValue: `Cruise · ${index + 1} of ${cruiseItems.length}`,
          })
          : t('itinerary.transportTypes.cruise', { defaultValue: 'Cruise' }),
        status: getReservationStatus(item, 'cruise', t),
        title: shipTitle,
        subtitle: portsLabel || (data.cruise_line && data.ship_name ? data.cruise_line : null)
          || (embarkPort ? <MapLink item={{ ...item, location: embarkPort }} fontSize={13} /> : null),
        summary: [formatShort(item.start_datetime), formatShort(item.end_datetime)].filter(Boolean).join(' → '),
        rows: [
          item.start_datetime && { label: t('itinerary.embarks'), value: formatShort(item.start_datetime) },
          item.end_datetime && { label: t('itinerary.disembarks'), value: formatShort(item.end_datetime) },
          data.cabin_number && { label: t('itinerary.cabin'), value: data.cabin_number },
          embarkPort && { label: t('itinerary.departurePort'), value: embarkPort },
        ].filter(Boolean),
        footerLeft: confirmation ? t('itinerary.refValue', { ref: confirmation }) : null,
        footerRight: nights != null ? t('itinerary.nights', { count: nights }) : null,
      });
    });

    // Other multi-day transport (train, ferry, bus, …) not already carded above.
    items
      .filter((item) => (
        isReservationOnlyItem(item)
        && item.category === 'transport'
        && !isFlightItineraryItem(item)
        && !isCarRentalItineraryItem(item)
        && !isCruiseItineraryItem(item)
      ))
      .forEach((item) => {
        const data = parseTransportData(item);
        const start = toLocalDate(item.start_datetime);
        const end = toLocalDate(item.end_datetime);
        const days = start && end ? Math.max(1, differenceInCalendarDays(end, start) + 1) : null;
        const confirmation = getConfirmationRef(item);
        result.push({
          key: `transport-${item.id}`,
          itemId: item.id,
          imageUrl: getItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
          fallbackSrc: getDefaultItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
          eyebrow: getCategoryEyebrow(item, t),
          status: getReservationStatus(item, 'transport', t),
          title: item.name,
          subtitle: item.location ? <MapLink item={item} fontSize={13} /> : null,
          summary: [formatShort(item.start_datetime), formatShort(item.end_datetime)].filter(Boolean).join(' → '),
          rows: [
            item.start_datetime && { label: t('itinerary.departs'), value: formatShort(item.start_datetime) },
            item.end_datetime && { label: t('itinerary.arrives'), value: formatShort(item.end_datetime) },
            data.operator && { label: t('itinerary.operator', { defaultValue: 'Operator' }), value: data.operator },
          ].filter(Boolean),
          footerLeft: confirmation ? t('itinerary.refValue', { ref: confirmation }) : null,
          footerRight: days != null ? t('itinerary.day', { count: days }) : null,
        });
      });

    const hotels = items.filter((item) => item.category === 'hotel');
    hotels.forEach((item, index) => {
      const start = toLocalDate(item.start_datetime);
      const end = toLocalDate(item.end_datetime);
      const nights = start && end ? Math.max(1, differenceInCalendarDays(end, start)) : null;
      const confirmation = getConfirmationRef(item);
      result.push({
        key: `hotel-${item.id}`,
        itemId: item.id,
        imageUrl: getItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
        fallbackSrc: getDefaultItineraryItemImageUrl(item, CARD_IMAGE_PARAMS),
        eyebrow: hotels.length > 1
          ? t('itinerary.stayIndexed', { current: index + 1, total: hotels.length, defaultValue: `Stay · ${index + 1} of ${hotels.length}` })
          : t('itinerary.hotel'),
        status: getReservationStatus(item, 'hotel', t),
        title: item.name,
        subtitle: item.location ? <MapLink item={item} fontSize={13} /> : null,
        summary: [formatShort(item.start_datetime), formatShort(item.end_datetime)].filter(Boolean).join(' → '),
        rows: [
          item.start_datetime && { label: t('itinerary.checkIn'), value: formatShort(item.start_datetime) },
          item.end_datetime && { label: t('itinerary.checkOut'), value: formatShort(item.end_datetime) },
        ].filter(Boolean),
        footerLeft: confirmation ? t('itinerary.refValue', { ref: confirmation }) : null,
        footerRight: nights != null ? t('itinerary.nights', { count: nights }) : null,
      });
    });

    return result;
  }, [items, t]);

  if (!cards.length) return null;

  return (
    <Box sx={{ mb: 5 }} data-testid="itinerary-reservations">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.75,
          pb: 1.4,
          mb: 2.25,
          borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
        }}
      >
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 21, fontWeight: 400, color: DOCUMENT.ink }}>
          {t('itinerary.reservations')}
        </Typography>
        <Typography sx={{ ml: 'auto', fontSize: 10, letterSpacing: '0.17em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
          {t('itinerary.confirmedCount', { count: cards.length })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {cards.map((card) => {
          const { key, ...cardProps } = card;
          const canModify = !canEditItem || canEditItem(card.itemId);
          return (
            <ReservationCard
              key={key}
              {...cardProps}
              flightStatusEnabled={flightStatusEnabled}
              onSelectItem={onSelectItem ? () => onSelectItem(card.itemId) : undefined}
              detailsLabel={t('itinerary.detailModal.details')}
              onEdit={onEditItem && canModify ? () => onEditItem(card.itemId) : undefined}
              editLabel={t('itinerary.editReservation')}
              onRemove={onRemoveItem && canModify ? () => onRemoveItem(card.itemId) : undefined}
              removeLabel={t('itinerary.removeReservation')}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ItineraryReservations;
